import React, { useState, useEffect } from 'react';
import RecipeCard from './RecipeCard';
import { useAuth } from '../context/AuthContext';
import styles from './MyRecipes.module.css';
import { useCSRF } from '../utils/csrfUtils';
import { Modal, Button } from 'react-bootstrap';
import supabase from '../supabaseClient';
import Masonry from 'react-masonry-css';
import AddRecipeModal from './AddRecipeModal';


function MyRecipes({ sessionToken, searchTerm = '', onMealAdded }) {
    const [myRecipes, setMyRecipes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [recipeToDelete, setRecipeToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [error, setError] = useState(null);
    const [showAddRecipeModal, setShowAddRecipeModal] = useState(false);
    const [addRecipeError, setAddRecipeError] = useState(null);
    const { pgUser } = useAuth();
    const { fetchWithCSRF, isLoading: csrfLoading } = useCSRF();
    const { sessionToken: authSessionToken, setSessionToken } = useAuth();

    // Wait for both session token and pgUser to be ready
    useEffect(() => {
        if (!sessionToken || !pgUser) {
            setIsLoading(true);
            return;
        }
        setIsLoading(false);
    }, [sessionToken, pgUser]);

    //if session token not passed from parent, get it from auth context
    useEffect(() => {
        if (!authSessionToken) {
            const getSessionToken = async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    setSessionToken(session.access_token);
                }
            };
            getSessionToken();
        }
    }, [authSessionToken]);


    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setRecipeToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        await handleDeleteRecipe(recipeToDelete.id);
        setIsDeleting(false);
        setShowDeleteModal(false);
        setRecipeToDelete(null);
    };

    const handleRecipeAdded = () => {
        setShowAddRecipeModal(false);
        window.location.reload(); // Refresh to show new recipe
    };

    const handleAddRecipeError = (errorMessage) => {
        setAddRecipeError(errorMessage);
    };
    
    const fetchFavorites = async () => {
        if (!sessionToken || !pgUser) return;

        try {
            const response = await fetch('/api/v1/recipe-favorite/my-favorites', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch favorites: ${response.status}`);
            }

            const data = await response.json();
            if (Array.isArray(data)) {
                setFavoriteRecipes(data);
            } else {
                console.error('Invalid data format for favorites');
                setFavoriteRecipes([]);
            }
        } catch (err) {
            console.error('Error fetching favorites.');
            // setError(err.message || 'Failed to load favorite recipes');
        } finally {
            setIsLoading(false);
        }
    };


    // Add delete handler
    const handleDeleteRecipe = async (recipeId) => {
        try {
            
            const response = await fetchWithCSRF(`/api/v1/recipe/${recipeId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete recipe');
            }

            // Update local state to remove the deleted recipe
            setMyRecipes(prevRecipes => prevRecipes.filter(recipe => recipe.id !== recipeId));
        } catch (err) {
            console.error('Error deleting recipe:', err);
            // Optionally show an error message to the user
        }
    };

    useEffect(() => {
        const fetchMyRecipes = async () => {
            if (!sessionToken || !pgUser) return;

            try {
                const response = await fetchWithCSRF('/api/v1/recipe/my-recipes', {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch recipes: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setMyRecipes(data);
                } else {
                    console.error('Invalid data format for recipes');
                    setMyRecipes([]);
                }
            } catch (err) {
                console.error('Error fetching recipes.');
                setError(err.message || 'Failed to load your recipes');
            } finally {
                setIsLoading(false);
            }
        };

        fetchMyRecipes();
        fetchFavorites();
    }, [pgUser, sessionToken, fetchWithCSRF]);

    if (isLoading) {
        return <div>Loading your recipes...</div>;
    }

    if (error) {
        return (
            <div className="alert alert-danger mt-4">
                <p className="mb-1">Error loading your recipes. Please refresh the page or try again later.</p>
                <p className="mb-1">{error}</p>
            </div>
        );
    }

    if (myRecipes.length === 0) {
        return (
            <div className="mt-3">
                You haven't created any recipes yet.{' '}
                <span 
                    onClick={() => setShowAddRecipeModal(true)}
                    style={{ color: '#52cd92', cursor: 'pointer', textDecoration: 'underline' }}
                >
                    Create one?
                </span>
                <AddRecipeModal 
                    isOpen={showAddRecipeModal} 
                    onClose={() => {
                        setShowAddRecipeModal(false);
                        setAddRecipeError(null);
                    }}
                    onSuccess={handleRecipeAdded}
                    onError={handleAddRecipeError}
                />
                {addRecipeError && (
                    <div className="alert alert-danger mt-3">
                        {addRecipeError}
                    </div>
                )}
            </div>
        );
    }

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        500: 1  // Changed to 1 column for very small screens
    };

    // If there's only one recipe, force single column layout
    if (myRecipes.length === 1) {
        breakpointColumns.default = 1;
    }

    // Filter recipes based on search term
    const filteredRecipes = myRecipes.filter(recipe => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search by recipe name
        if (recipe.name.toLowerCase().includes(searchLower)) {
            return true;
        }
        
        // Search by ingredient names (if ingredients are available)
        if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
            return recipe.ingredients.some(ingredient => 
                ingredient.ingredient_name && 
                ingredient.ingredient_name.toLowerCase().includes(searchLower)
            );
        }
        
        return false;
    });

    if (filteredRecipes.length === 0 && searchTerm) {
        return <div className="text-center my-5">No recipes found matching "{searchTerm}"</div>;
    }

    return (
        <div className={styles.container}>
            <Masonry
                breakpointCols={breakpointColumns}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {filteredRecipes.map(recipe => (
                    <div 
                        key={recipe.id} 
                        className={styles.masonryItem} 
                        style={{ 
                            position: 'relative',
                            minWidth: '250px',
                            width: '100%'
                        }}
                    >
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteModal(true);
                                setRecipeToDelete(recipe);
                            }}
                            style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                zIndex: 2,
                                background: 'transparent',
                                color: 'black',
                                border: 'none',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            <img src={"/icons/x.svg"} alt="Delete" />
                        </button>
                        <RecipeCard 
                            recipe={recipe}
                            isFavorite={favoriteRecipes.some(favorite => favorite.recipe_id === recipe.id)}
                            onMealAdded={onMealAdded}
                        />
                    </div>
                ))}
            </Masonry>


            {/* Delete Recipe Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Delete {recipeToDelete?.name}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDeleteCancel}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Recipe Modal */}
            <AddRecipeModal 
                isOpen={showAddRecipeModal} 
                onClose={() => {
                    setShowAddRecipeModal(false);
                    setAddRecipeError(null);
                }}
                onSuccess={handleRecipeAdded}
                onError={handleAddRecipeError}
            />

            {/* Add Recipe Error */}
            {addRecipeError && (
                <div className="alert alert-danger mt-3">
                    {addRecipeError}
                </div>
            )}
        </div>
    );
}

export default MyRecipes;
